import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import { BlAuthGuard, BlErrorGuard } from '@blcloud/bl-ng-common';
import {
  BrowserGuard,
  ErrorComponent,
  ErrorDeactivateGuard,
  ServiceCondition,
  ServiceGuard,
  PermissionGuard,
  ContractGuard,
  DeviceSessionGuard,
  PageId
} from '@blcloud/bl-ng-share-module';
import { LoginPageComponent } from './page/login-page/login-page.component';
import { RedirectPageComponent } from './page/redirect-page/redirect-page.component';

/** ルーティング */
const routes: Routes = [
  { path: 'error', component: ErrorComponent, canActivate: [BlErrorGuard], canDeactivate: [ErrorDeactivateGuard] },  // 共通エラー画面

  // 業務メニュー
  {
    path: 'biz',
    loadChildren: './page/biz-menu-page/biz-menu-page.module#BizMenuPageModule',
    canLoad: [BrowserGuard, BlAuthGuard, ContractGuard]
  },
  // 基本情報設定ウィザード
  {
    path: 'wizard',
    loadChildren: './page/wizard-page/wizard-page.module#WizardPageModule',
    canLoad: [BlAuthGuard, ContractGuard, PermissionGuard],
    data: { blFunctionId: 'BM100-121900-E' }
  },
  // 伝票検索
  {
    path: 'salesslip/search',
    loadChildren: './page/sales-slip-search-page/sales-slip-search-page.module#SalesSlipSearchPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 伝票履歴
  {
    path: 'salesslip/history',
    loadChildren: './page/sales-slip-history-page/sales-slip-history-page.module#SalesSlipHistoryPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 伝票履歴検索
  {
    path: 'salesslip/history/search',
    loadChildren: './page/sales-slip-history-search-page/sales-slip-history-search-page.module#SalesSlipHistorySearchPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 受付
  {
    path: 'reception/entry',
    loadChildren: './page/recept-entry-page/recept-entry-page.module#ReceptEntryPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 顧客照会
  {
    path: 'customerinquiry',
    loadChildren: 'app/page/customer-inquiry-page/customer-inquiry-page.module#CustomerInquiryPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 顧客入力画面
  {
    path: 'customer/entry',
    loadChildren: 'app/page/customer-entry-page/customer-entry-page.module#CustomerEntryPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard, DeviceSessionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_CUSTOMER_MGT,
      pageId: PageId.CustomerEntry
    }
  },
  // 車両入力画面
  {
    path: 'vehicle/entry',
    loadChildren: 'app/page/vehicle-entry-page/vehicle-entry-page.module#VehicleEntryPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard, DeviceSessionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_VEHICLE_MGT,
      pageId: PageId.VehicleEntry
    }
  },
  // 顧客車両検索画面
  {
    path: 'vehicle/search',
    // AVIS.C start
    loadChildren: 'app/page/ab-customer-vehicle-search-page/ab-customer-vehicle-search-page.module#AbCustomerVehicleSearchPageModule',
    // AVIS.C end
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: [ContractConst.SERVICEID_OPT_CMN_CUSTOMER_MGT, ContractConst.SERVICEID_OPT_CMN_VEHICLE_MGT],
      condition: ServiceCondition.And
    }
  },
  // 予約詳細
  {
    path: 'reserve/entry',
    loadChildren: 'app/page/reserve-entry-page/reserve-entry-page.module#ReserveEntryPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: [ContractConst.SERVICEID_OPT_CMN_RESERVE_MGT],
    }
  },
  { path: 'contact', pathMatch: 'full', redirectTo: 'contact/callList' },
  // コンタクト一覧
  {
    path: 'contact/contactList',
    loadChildren: 'app/page/contact-list-page/contact-list-page.module#ContactListPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // コンタクトカレンダー
  {
    path: 'contact/contactCalendar',
    loadChildren: 'app/page/contact-calendar-page/contact-calendar-page.module#ContactCalendarPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // 架電一覧
  {
    path: 'contact/callList',
    loadChildren: 'app/page/call-list-page/call-list-page.module#CallListPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_CONTACT_SCHEDULE_MAIN
    }
  },
  // プロフィール登録一覧
  {
    path: 'profile/entries',
    loadChildren: './page/profile-entries-page/profile-entries-page.module#ProfileEntriesPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_PROFILE_ENTRY,
    }
  },
  // 一括編集
  {
    path: 'bulkedit',
    loadChildren: 'app/page/bulk-edit-page/bulk-edit-page.module#BulkEditPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },
  // フォロワー(LINE登録)一覧
  {
    path: 'follower/search',
    loadChildren: 'app/page/follower-search-page/follower-search-page.module#FollowerSearchPageModule',
    canLoad: [BlAuthGuard, ContractGuard, ServiceGuard, PermissionGuard],
    data: {
      serviceId: ContractConst.SERVICEID_OPT_CMN_LINE_BASE,
      blFunctionId: RoleConst.ROLEID_FUNC_ALL_LINE_FOLLOW_LIST
    }
  },

  // LINEお友達登録QR
  {
    path: 'line-qr',
    loadChildren: 'app/page/line-qr-page/line-qr-page.module#LineQrPageModule',
    canLoad: [BlAuthGuard, ContractGuard],
  },

  // ローン審査一覧画面
  {
    path: 'loan/search',
    loadChildren: 'app/page/loan-application-search-page/loan-application-search-page.module#LoanApplicationSearchPageModule',
    canActivate: [BlAuthGuard]
  },

  // SPA振り分けリダイレクトページ：通知トリガー
  {
    path: 'notice/redirect',
    loadChildren: 'app/page/notice-redirect-page/notice-redirect-page.module#NoticeRedirectPageModule',
    canActivate: [BlAuthGuard]
  },

  { path: 'login', component: LoginPageComponent, canActivate: [BrowserGuard] },
  { path: 'redirect', component: RedirectPageComponent },
  // Default
  { path: '', pathMatch: 'full', redirectTo: 'biz' },
  { path: '**', redirectTo: 'biz' }
];

/**
 * アプリケーションルーティングモジュール
 */
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}



// WEBPACK FOOTER //
// ./src/app/app-routing.module.ts